import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink, Loader2, Lock, Phone, RefreshCw } from "lucide-react";
import Seo from "../components/Seo";
import { createPaymentSession, paymentReturn, abandonOrder } from "../lib/api";
import { clearPendingOrder } from "../lib/pendingOrders";
import { orderEmail } from "../lib/orderRef";
import { money } from "../lib/format";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

/** Are we inside an iframe (e.g. the embedded preview)? Stripe Checkout REFUSES
 *  to run framed -- "Stripe Checkout is not able to run in an iFrame. Please
 *  redirect to Checkout at the top level." -- so an in-frame navigation silently
 *  fails and strands the customer on an unpaid order. */
const isFramed = () => {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which itself means we are framed.
    return true;
  }
};

/** Break out of the frame: try the top-level location first, then a new tab. */
const escapeFrame = (url) => {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return "top";
    }
  } catch {
    /* cross-origin parent: not permitted, fall through to a new tab */
  }
  const win = window.open(url, "_blank", "noopener,noreferrer");
  return win ? "new-tab" : "blocked";
};

export default function PaymentHandoff() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  const cancelled = params.get("cancelled") === "1";
  // Set by checkout when the optional account opt-in was used: "signed-in" or
  // "pending". Deliberately says nothing about WHY a sign-in didn't happen.
  const acct = params.get("acct");
  const [state, setState] = useState({ loading: !cancelled, error: null, session: null });
  // "top" | "new-tab" | "blocked" -- only set when we are framed.
  const [breakout, setBreakout] = useState(null);
  const [status, setStatus] = useState({ checking: false, result: null });
  const started = useRef(false);
  const framed = isFramed();
  // The platform's lookup takes the consumer's email as an input and we store
  // none, so a reminder link opened in a LATER browser session has no email to
  // send. Sending them to the confirmation page, which asks for it, beats failing.
  const email = orderEmail(code);

  const start = useCallback(async () => {
    setState({ loading: true, error: null, session: null });
    try {
      const session = await createPaymentSession(code, email);
      setState({ loading: false, error: null, session });
      if (!session.checkoutUrl) return;

      if (isFramed()) {
        // NEVER attempt an in-frame navigation: Stripe refuses to render and the
        // customer is left looking at a blank frame with a live unpaid order.
        setBreakout(escapeFrame(session.checkoutUrl));
        return;
      }
      if (session.presentation === "popup") {
        const win = window.open(session.checkoutUrl, "wbm-payment", "width=520,height=760");
        if (!win) window.location.assign(session.checkoutUrl);
        return;
      }
      window.location.assign(session.checkoutUrl);
    } catch (err) {
      setState({ loading: false, error: err.payload || { userMessage: err.message }, session: null });
    }
  }, [code, email]);

  useEffect(() => {
    if (cancelled || started.current || !code || !email) return;
    started.current = true;
    start();
  }, [cancelled, code, email, start]);

  /** Landing on our cancel URL means the customer walked away from Checkout. The
   *  order still sits at the platform's status 0, which BLOCKS the boat's
   *  availability, so the hold is released (status -1) rather than left to a
   *  server-side sweep. Read-only for an order the platform reports as paid. */
  const abandoned = useRef(false);
  useEffect(() => {
    if (!cancelled || abandoned.current || !code || !email) return;
    abandoned.current = true;
    abandonOrder(code, email)
      .then((r) => {
        if (r.abandoned) clearPendingOrder(code);
      })
      .catch(() => {
        /* the platform also sweeps stale unpaid holds; nothing to tell the customer */
      });
  }, [cancelled, code, email]);

  /** Payment may well have completed in ANOTHER tab, so the original tab must be
   *  able to pick the result up from the vendor's own summary. The vendor resolves
   *  the payment inline on this read, so it is asked ONCE per click -- it never
   *  polls, and it never asserts payment itself. */
  const refreshStatus = async () => {
    setStatus({ checking: true, result: null });
    try {
      const d = await paymentReturn(code, email);
      const settled = d.state === "paid";
      if (settled) clearPendingOrder(code);
      setStatus({ checking: false, result: settled ? "paid" : "pending" });
    } catch {
      setStatus({ checking: false, result: "unknown" });
    }
  };

  const error = state.error;
  const checkoutUrl = state.session?.checkoutUrl;
  const showInterstitial = framed && checkoutUrl && !state.session?.alreadyPaid;

  return (
    <>
      <Seo title="Secure payment | West Branch Marina" path="/booking/payment" noindex />
      <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center px-5 py-28 lg:px-10">
        <div data-testid="payment-handoff" className="rounded-3xl border border-sand-border bg-white p-8 text-center">
          <p className="eyebrow text-lake">Order {code || "—"}</p>

          {acct === "signed-in" && (
            <p data-testid="handoff-account-created" role="status" className="mt-4 rounded-xl border border-lake/40 bg-lake/[0.07] p-3 text-sm text-navy">
              Your account is set up and you're signed in — this trip will appear in My trips. A welcome
              email is on its way, but you don't need it to use your account.
            </p>
          )}
          {acct === "pending" && (
            <p data-testid="handoff-account-pending" role="status" className="mt-4 rounded-xl border border-sand-border bg-sand-card p-3 text-sm text-slate-700">
              Your reservation is confirmed. Your account needs a sign-in before your trips appear —
              check your email, then sign in with the password you chose.
            </p>
          )}

          {/* FRAMED (embedded preview): Stripe cannot render here, so we break out
              automatically and explain what happened. */}
          {showInterstitial && (
            <div data-testid="payment-iframe-interstitial" className="mt-4">
              <h1 className="font-display text-3xl font-extrabold text-navy">
                Payment opens in a new tab for security
              </h1>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-600">
                {breakout === "top"
                  ? "We're taking you to our secure payment provider now."
                  : breakout === "new-tab"
                    ? "We've opened our secure payment provider in a new tab. Finish there, then come back to this page."
                    : "Your browser blocked the new tab. Use the button below to open our secure payment provider."}
              </p>
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="payment-open-secure-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
              >
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                Open secure payment
              </a>
              <p className="mt-6 text-sm text-slate-600">
                Your reservation code is{" "}
                <code data-testid="payment-interstitial-code" className="font-bold text-navy">{code}</code> — your
                boats are held while you pay.
              </p>
              <button
                type="button"
                data-testid="payment-refresh-status-button"
                onClick={refreshStatus}
                disabled={status.checking}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:bg-sand-card disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${status.checking ? "animate-spin" : ""}`} aria-hidden="true" />
                {status.checking ? "Checking…" : "I've completed payment — refresh status"}
              </button>
              {status.result === "paid" && (
                <p data-testid="payment-status-paid" role="status" className="mt-4 text-sm font-bold text-navy">
                  Payment confirmed.{" "}
                  <Link to={`/booking/confirmation?code=${code}`} className="text-lake underline">
                    View your confirmation
                  </Link>
                </p>
              )}
              {status.result === "pending" && (
                <p data-testid="payment-status-pending" role="status" className="mt-4 text-sm text-slate-600">
                  No payment has reached our booking system yet. If you've just paid, give it a moment and
                  check again — we only confirm once our booking system says the money arrived.
                </p>
              )}
              {status.result === "unknown" && (
                <p data-testid="payment-status-unknown" role="status" className="mt-4 text-sm text-slate-600">
                  We couldn't check just now. Please try again in a moment, or call {WB.phone}.
                </p>
              )}
            </div>
          )}

          {cancelled && !state.session && (
            <>
              <h1 className="mt-3 font-display text-3xl font-extrabold text-navy">
                Payment cancelled — your reservation has been released
              </h1>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-600">
                Nothing has been charged.
              </p>
              {/* The order is Abandoned and its hold is gone, so there is nothing to
                  resume: the only honest next step is a fresh booking. */}
              <Link
                to="/boat-rentals/west-branch"
                data-testid="payment-new-booking-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Start a new booking
              </Link>
            </>
          )}

          {!cancelled && !email && code && (
            <div data-testid="handoff-needs-email">
              <h1 className="mt-3 font-display text-3xl font-extrabold text-navy">
                Let's find your order first
              </h1>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-600">
                To pay securely we need to look order{" "}
                <strong className="font-bold text-navy">{code}</strong> up in our booking system, and
                that needs the email address you booked with.
              </p>
              <Link
                to={`/booking/confirmation?code=${encodeURIComponent(code)}`}
                data-testid="handoff-supply-email"
                className="mt-7 inline-block rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
              >
                Continue
              </Link>
            </div>
          )}

          {email && state.loading && (
            <>
              <h1 className="mt-3 font-display text-3xl font-extrabold text-navy">
                Taking you to our secure payment provider
              </h1>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-600">
                Your card details are handled entirely by our payment provider — they never touch our
                servers. Please don't close this window.
              </p>
              <Loader2 data-testid="payment-spinner" className="mx-auto mt-8 h-7 w-7 animate-spin text-lake" aria-hidden="true" />
            </>
          )}

          {state.session?.alreadyPaid && (
            <>
              <h1 className="mt-3 font-display text-3xl font-extrabold text-navy">This order is paid</h1>
              <p className="mt-4 text-sm text-slate-600">There's nothing left to pay on {code}.</p>
              <Link
                to={`/booking/confirmation?code=${code}`}
                data-testid="payment-view-confirmation"
                className="mt-7 inline-block rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
              >
                View your confirmation
              </Link>
            </>
          )}

          {checkoutUrl && !framed && state.session.presentation === "popup" && (
            <p className="mt-6 text-sm text-slate-600">
              A secure payment window has opened. If you don't see it,{" "}
              <a
                href={checkoutUrl}
                data-testid="payment-manual-link"
                className="font-bold text-lake underline"
              >
                open it here
              </a>
              .
            </p>
          )}

          {error && (
            <>
              <h1 className="mt-3 font-display text-3xl font-extrabold text-navy">
                We couldn't start the card payment
              </h1>
              <p data-testid="payment-error" className="mx-auto mt-4 max-w-md text-sm text-slate-600">
                {error.userMessage}
              </p>
              {error.payAtMarina && (
                <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
                  Your reservation <strong className="font-bold text-navy">{code}</strong> is held. The balance
                  is payable at the marina.
                </p>
              )}
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  data-testid="payment-retry-button"
                  onClick={start}
                  className="rounded-full border border-navy/20 px-7 py-3.5 font-bold text-navy hover:bg-sand-card"
                >
                  Try again
                </button>
                <a
                  href={WB.phoneHref}
                  data-testid="payment-call-link"
                  className="inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call {WB.phone}
                </a>
              </div>
            </>
          )}

          {state.session?.amount > 0 && !error && (
            <p className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {state.session.type === "deposit" ? "Deposit due today" : "Amount due"}:{" "}
              {money(state.session.amount)}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
